import { colors } from "./colors";
import { AvailabilityStatus, BreakMode, CompanyStatus, EmployeeStatus, EmploymentType, Gender, MimeType, RevenueType, StaffAppFeature, TaxKey, } from "./model.types";
import { getMaxPay } from "./salary";
import { toDateString } from "./utils";
export function breakModeLabel(mode) {
    switch (mode) {
        case BreakMode.Auto:
            return "Automatisch";
        case BreakMode.Manual:
            return "Manuell";
        case BreakMode.AutoPlusManual:
            return "Automatisch + Manuell";
        case BreakMode.Planned:
            return "Geplant";
        case BreakMode.PlannedPlusManual:
            return "Geplant + Manuell";
        case BreakMode.AutoOrManual:
            return "Automatisch oder Manuell";
        case BreakMode.PlannedOrManual:
            return "Geplant oder Manuell";
    }
}
export function employmentTypeLabel(type, full = false) {
    return full
        ? [
            "Vollzeit/Teilzeit",
            "Aushilfe",
            "Freier Mitarbeiter",
            "Kurzfristig Beschäftigt",
            "Auszubildender",
            "Midi-Job",
            "Werkstudent",
            "Dualer Student",
            "Praktikant",
        ][type]
        : [
            "VZ/TZ",
            "Aushilfe",
            "Fr. Mitarb.",
            "Kurzfr. Besch.",
            "Azubi",
            "Midi-Job",
            "Werkstudent",
            "Dualer Stud.",
            "Praktikant",
        ][type];
}
export function employmentTypeDescription(type, country, date = new Date()) {
    switch (type) {
        case EmploymentType.Regular:
            return "Der Mitarbeiter ist normal angestellt und bezieht ein monatliches oder stündliches Gehalt.";
        case EmploymentType.Marginal: {
            const maxPay = getMaxPay(EmploymentType.Marginal, date, country);
            return `Der Mitarbeiter ist geringfügig beschäftigt und darf nicht mehr als ${maxPay} € im Monat verdienen.`;
        }
        case EmploymentType.Independent:
            return "Der Mitarbeiter ist auf selbständiger Basis tätig und schreibt Rechnungen.";
        case EmploymentType.ShortTerm:
            return "Kurzfristige Beschäftigte und Saisonarbeiter dürfen nicht mehr als 70 Tage / Jahr beschäftigt werden.";
        case EmploymentType.Trainee:
            return "Bei Auszubildenden ist die Mindestlohn-Überprüfung ausgesetzt. Ansonsten gelten altersunabhängig die Standardregeln für Festangestellte.";
        case EmploymentType.MidiJob: {
            const marginalMax = getMaxPay(EmploymentType.Marginal, date, country);
            const marginalMaxPlusOneCent = (marginalMax + 0.01).toFixed(2);
            return `Ein Midi-Job ist eine geringfügige Beschäftigung mit einem monatlichen Arbeitsentgelt zwischen ${marginalMaxPlusOneCent} € und 2000 €. Midijobber sind versicherungspflichtig und dürfen maximal 155,9 Stunden im Monat arbeiten.`;
        }
        case EmploymentType.WorkingStudent:
            return "Werkstudenten sind an einer Fach- oder Hochschule Studierende, die neben dem Studium einer geringfügigen Beschäftigung nachgehen. Werkstudenten dürfen höchsten 20 Stunden pro Woche während der Vorlesungszeit und insgesamt nicht mehr als 26 Wochen im Jahr arbeiten.";
        case EmploymentType.DualStudent:
            return "Duales Studenten sind an einer Hochschule Studierende, die ihr Studium mit Praxiserfahrung in einem Unternehmen kombinieren. Im Gegensatz zu Werkstudenten sind duale Studenten voll sozialversicherungspflichtig und es gibt keine besonderen Einschränkungen der Arbeitszeit.";
        case EmploymentType.Intern:
            return "Praktikanten sind Personen, die eine in Studien- oder Prüfungsordnungen vorgeschriebene berufspraktische Tätigkeit im Rahmen eines Praktikums verrichten.";
        default:
            return "";
    }
}
export function employmentTypeColor(type) {
    switch (type) {
        case EmploymentType.Regular:
            return colors.blue;
        case EmploymentType.Marginal:
            return colors.orange;
        case EmploymentType.Independent:
            return colors.violet;
        case EmploymentType.ShortTerm:
            return colors.green;
        case EmploymentType.Trainee:
            return colors.yellow;
        case EmploymentType.MidiJob:
            return colors.purple;
        case EmploymentType.WorkingStudent:
            return colors.teal;
        case EmploymentType.DualStudent:
            return colors.red;
        case EmploymentType.Intern:
            return colors.pink;
        default:
            return "";
    }
}
export function getEmploymentTypeGroup(type) {
    switch (type) {
        case EmploymentType.Regular:
            return "101";
        case EmploymentType.Marginal:
            return "109";
        case EmploymentType.ShortTerm:
            return "110";
        case EmploymentType.Trainee:
            return "121";
        case EmploymentType.MidiJob:
            return "";
        case EmploymentType.WorkingStudent:
            return "106";
        case EmploymentType.DualStudent:
            return "102";
        case EmploymentType.Intern:
            return "105";
        case EmploymentType.Independent:
            return "";
        default:
            return "";
    }
}
export function getEmploymentTypes(country, date = new Date()) {
    return Object.values(EmploymentType)
        .filter((t) => typeof t === "number")
        .map((type) => ({
        type,
        label: employmentTypeLabel(type, true),
        labelShort: employmentTypeLabel(type),
        description: employmentTypeDescription(type, country || "DE", date),
        color: employmentTypeColor(type),
    }));
}
export function staffAppFeatureLabel(feature) {
    switch (feature) {
        case StaffAppFeature.Roster:
            return "Dienstplan";
        case StaffAppFeature.Absences:
            return "Abwesenheiten";
        case StaffAppFeature.Ledgers:
            return "Konten";
        case StaffAppFeature.Time:
            return "Arbeitszeiten";
        case StaffAppFeature.Tracking:
            return "Stempeluhr";
    }
}
export function employeeStatusLabel(status) {
    switch (status) {
        case EmployeeStatus.Probation:
            return "Auf Probe";
        case EmployeeStatus.Active:
            return "Aktiv";
        case EmployeeStatus.Retired:
            return "Ausgeschieden";
    }
}
export function employeeStatusColor(status) {
    switch (status) {
        case EmployeeStatus.Probation:
            return "orange";
        case EmployeeStatus.Active:
            return "green";
        case EmployeeStatus.Retired:
            return "red";
    }
}
export function employeeStatusIcon(status) {
    switch (status) {
        case EmployeeStatus.Probation:
            return "icon.status.probation";
        case EmployeeStatus.Active:
            return "icon.status.employed";
        case EmployeeStatus.Retired:
            return "icon.status.retired";
    }
}
export function genderLabel(gender) {
    switch (gender) {
        case Gender.Male:
            return "männlich";
        case Gender.Female:
            return "weiblich";
        case Gender.NonBinary:
            return "divers";
        case Gender.Undetermined:
            return "unbestimmt";
    }
}
export function companyStatusColor(status) {
    switch (status) {
        case CompanyStatus.TrialWithoutContract:
            return colors.grey;
        case CompanyStatus.Trialing:
            return colors.blue;
        case CompanyStatus.Probation:
            return colors.purple;
        case CompanyStatus.Active:
            return colors.green;
        case CompanyStatus.Paused:
            return colors.orange;
        case CompanyStatus.Canceled:
            return colors.red;
        case CompanyStatus.PaymentOverdue:
            return colors.red;
        default:
            return "";
    }
}
export function companyStatusLabel(status) {
    switch (status) {
        case CompanyStatus.TrialWithoutContract:
            return "Testversion";
        case CompanyStatus.Trialing:
            return "Testphase";
        case CompanyStatus.Probation:
            return "Probezeit";
        case CompanyStatus.Active:
            return "Aktiv";
        case CompanyStatus.Paused:
            return "Pausiert";
        case CompanyStatus.Canceled:
            return "Gekündigt";
        case CompanyStatus.PaymentOverdue:
            return "Zahlungsverzug";
        default:
            return "";
    }
}
export function availabilityColor(status) {
    switch (status) {
        case AvailabilityStatus.Available:
            return colors.green;
        case AvailabilityStatus.Unavailable:
            return colors.red;
        case AvailabilityStatus.Preferred:
            return colors.purple;
        case AvailabilityStatus.Unpreferred:
            return colors.orange;
        default:
            return "";
    }
}
export function availabilityLabel(status) {
    switch (status) {
        case AvailabilityStatus.Available:
            return "Verfügbar";
        case AvailabilityStatus.Unavailable:
            return "Nicht Verfügbar";
        case AvailabilityStatus.Preferred:
            return "Bevorzugt";
        case AvailabilityStatus.Unpreferred:
            return "Wunschfrei";
        default:
            return "";
    }
}
export function availabilityIcon(status) {
    switch (status) {
        case AvailabilityStatus.Available:
            return "icon.status.available";
        case AvailabilityStatus.Unavailable:
            return "icon.status.unavailable";
        case AvailabilityStatus.Preferred:
            return "icon.status.preferred";
        case AvailabilityStatus.Unpreferred:
            return "icon.status.unpreferred";
    }
}
export function repeatsLabel(repeats) {
    switch (repeats) {
        case "never":
            return "niemals";
        case "weekly":
            return "wöchtentlich";
        case "monthly":
            return "monatlich";
        case "yearly":
            return "jährlich";
    }
}
export function taxKeyLabel(key, date) {
    const specificDateOrToday = date || toDateString(new Date());
    const inReducedTaxRange = specificDateOrToday >= "2020-07-01" && specificDateOrToday < "2021-01-01";
    switch (key) {
        case TaxKey.None:
            return "";
        case TaxKey.Free:
            return "Steuerfrei";
        case TaxKey.Sales7:
            return inReducedTaxRange ? "USt. 5%" : "USt. 7%";
        case TaxKey.Sales19:
            return inReducedTaxRange ? "USt. 16%" : "USt. 19%";
        case TaxKey.Input7:
            return inReducedTaxRange ? "VSt. 5%" : "VSt. 7%";
        case TaxKey.Input19:
            return inReducedTaxRange ? "VSt. 16%" : "VSt. 19%";
        case TaxKey.Input7_8:
            return "VSt. 7,8%";
        case TaxKey.Input9_5:
            return date ? (date >= "2022-01-01" ? "VSt. 9,5%" : "VSt. 10,7%") : "VSt. 9,5/10,7%";
        default:
            return "";
    }
}
export function taxKeyValue(key, date) {
    const reduced = date && date >= "2020-07-01" && date < "2021-01-01";
    switch (key) {
        case TaxKey.None:
        case TaxKey.Free:
            return 0;
        case TaxKey.Sales7:
        case TaxKey.Input7:
            return reduced ? 5 : 7;
        case TaxKey.Sales19:
        case TaxKey.Input19:
            return reduced ? 16 : 19;
        case TaxKey.Input7_8:
            return 7.8;
        case TaxKey.Input9_5:
            return date && date >= "2022-01-01" ? 9.5 : 10.7;
        default:
            return 0;
    }
}
export function revenueTypeLabel(type) {
    switch (type) {
        case RevenueType.Sales:
            return "Einnahme";
        case RevenueType.Expense:
            return "Ausgabe";
        case RevenueType.PayAdvance:
            return "Gehaltsvorschuss";
        case RevenueType.BankDeposit:
            return "Bankeinzahlung";
        case RevenueType.Deposit:
            return "Einzahlung";
        case RevenueType.CashCount:
            return "Kassenzählung";
        case RevenueType.Debt:
            return "Rechnung";
        case RevenueType.Adjustment:
            return "Ausgleichsbuchung";
        case RevenueType.Cashless:
            return "Bargeldlose Zahlung";
        case RevenueType.Other:
            return "Sonstiges";
        default:
            break;
    }
}
export function revenueTypeIcon(type) {
    switch (type) {
        case RevenueType.Sales:
            return "icon.object.revenue";
        case RevenueType.Expense:
            return "icon.object.expense";
        case RevenueType.PayAdvance:
            return "icon.object.pay-advance";
        case RevenueType.BankDeposit:
            return "icon.object.bank-deposit";
        case RevenueType.CashCount:
            return "icon.object.cash-count";
        case RevenueType.Debt:
            return "icon.object.receivable";
        case RevenueType.Adjustment:
            return "icon.object.revenue-adjustment";
        case RevenueType.Cashless:
            return "icon.object.payment-method";
        case RevenueType.Other:
            return "icon.object.currency";
        default:
            return "icon.object.revenue-other";
    }
}
export function fileIcon(type) {
    switch (type) {
        case MimeType.PDF:
            return "icon.file.pdf";
        case MimeType.TXT:
            return "icon.file.plain-text";
        case MimeType.CSV:
            return "icon.file.csv";
        case MimeType.MSExcel:
        case MimeType.MSExcelLegacy:
            return "icon.file.spreadsheet";
        case MimeType.MSWord:
        case MimeType.MSWordLegacy:
            return "icon.file.text";
        case MimeType.PNG:
        case MimeType.JPG:
            return "icon.file.image";
        case MimeType.Zip:
            return "icon.file.archive";
        default:
            return "icon.file.document";
    }
}
export function fileExtension(type) {
    switch (type) {
        case MimeType.PDF:
            return "pdf";
        case MimeType.TXT:
            return "txt";
        case MimeType.CSV:
            return "csv";
        case MimeType.MSExcel:
            return "xlsx";
        case MimeType.MSExcelLegacy:
            return "xls";
        case MimeType.MSWord:
            return "docx";
        case MimeType.MSWordLegacy:
            return "doc";
        case MimeType.PNG:
            return "png";
        case MimeType.JPG:
            return "jpg";
        case MimeType.WEBP:
            return "webp";
        case MimeType.GIF:
            return "gif";
        case MimeType.Zip:
            return "zip";
        case MimeType.HTML:
            return "html";
        case MimeType.XML:
            return "xml";
        case MimeType.Markdown:
            return "md";
        case MimeType.JSON:
            return "json";
        case MimeType.RTF:
            return "rtf";
        case MimeType.ODT:
            return "odt";
        case MimeType.MSPowerPointLegacy:
            return "ppt";
        case MimeType.MSPowerPoint:
            return "pptx";
        case MimeType.NONE:
        case undefined:
        default:
            return "file";
    }
}
export function stripSensitiveData(e) {
    e.address = "";
    e.birthCity = "";
    e.birthCountry = "";
    e.birthName = "";
    e.birthday = null;
    e.city = "";
    e.email = "";
    e.phone = "";
    e.phone2 = "";
    e.notes = "";
    e.postalCode = "";
    e.socialSecurityNumber = "";
    e.taxId = "";
    e.gender = undefined;
    e.nationality = "";
    e.contracts.forEach((c) => {
        c.benefits = [];
        c.salaries = [];
        c.bonusHoliday = 0;
        c.bonusNight1 = 0;
        c.bonusNight2 = 0;
        c.bonusSpecial = 0;
        c.bonusSunday = 0;
        c.bonuses = [];
        c.comment = "";
        c.enableSFNLedger = false;
        c.maxSalary = 0;
        c.sfnAdvance = 0;
    });
    e.invite = e.invite ? { ...e.invite, id: "" } : null;
}
export function stripSensitiveDataForManageApp(e) {
    e.invite = e.invite ? { ...e.invite, id: "" } : null;
}
//# sourceMappingURL=model.functions.js.map